import { logger } from '../utils/logger';
import api from '../utils/api';

/**
 * Interface pour les erreurs capturées
 */
export interface ErrorReport {
  error: string;
  stack: string;
  componentStack?: string;
  timestamp: Date;
  url: string;
  userAgent: string;
  userId?: string;
  tenantId?: string;
  additionalContext?: Record<string, any>;
}

/**
 * Service de reporting d'erreurs
 *
 * Gère la collecte et l'envoi des erreurs à un service de monitoring.
 * Stocke localement les erreurs en cas d'échec d'envoi.
 */
class ErrorReportingService {
  private readonly MAX_LOCAL_ERRORS = 50;
  private readonly LOCAL_STORAGE_KEY = 'error_reports';

  /**
   * Log une erreur et l'envoie au backend
   */
  async logError(errorReport: ErrorReport): Promise<void> {
    try {
      // Log en console (dev)
      if (import.meta.env.DEV) {
        logger.error('ErrorBoundary caught error:', {
          error: errorReport.error,
          stack: errorReport.stack,
          componentStack: errorReport.componentStack,
          url: errorReport.url,
        });
      }

      // Stocke localement
      this.storeLocalError(errorReport);

      // Envoie au backend (si endpoint disponible)
      await this.sendToBackend(errorReport);
    } catch (err) {
      // Si l'envoi échoue, l'erreur est déjà stockée localement
      logger.error('Failed to send error report:', err);
    }
  }

  /**
   * Envoie l'erreur au backend
   * Appelle POST /api/v1/errors/report avec le rapport d'erreur
   */
  private async sendToBackend(errorReport: ErrorReport): Promise<void> {
    try {
      await api.post('/errors/report', {
        error: errorReport.error,
        stack: errorReport.stack,
        componentStack: errorReport.componentStack,
        timestamp: errorReport.timestamp instanceof Date
          ? errorReport.timestamp.toISOString()
          : errorReport.timestamp,
        url: errorReport.url,
        userAgent: errorReport.userAgent,
        additionalContext: errorReport.additionalContext,
      });
    } catch (err) {
      // Silently fail - l'erreur est déjà stockée localement
      // Avoid re-throwing to prevent infinite error loops when
      // the error reporting endpoint itself fails
      if (import.meta.env.DEV) {
        logger.warn('Failed to send error report to backend:', err);
      }
    }
  }

  /**
   * Stocke l'erreur localement (localStorage)
   */
  private storeLocalError(errorReport: ErrorReport): void {
    try {
      const errors = this.getLocalErrors();

      // Ajoute la nouvelle erreur
      errors.unshift({
        ...errorReport,
        timestamp: errorReport.timestamp.toISOString(),
      });

      // Limite le nombre d'erreurs stockées
      const trimmedErrors = errors.slice(0, this.MAX_LOCAL_ERRORS);

      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(trimmedErrors));
    } catch (err) {
      // Si localStorage est plein ou désactivé, ignore silencieusement
      logger.error('Failed to store error locally:', err);
    }
  }

  /**
   * Récupère les erreurs stockées localement
   */
  getLocalErrors(): any[] {
    try {
      const stored = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      return [];
    }
  }

  /**
   * Nettoie les erreurs locales
   */
  clearLocalErrors(): void {
    try {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    } catch (err) {
      logger.error('Failed to clear local errors:', err);
    }
  }

  /**
   * Log une erreur API (pour les erreurs HTTP)
   */
  async logApiError(error: {
    endpoint: string;
    method: string;
    status: number;
    message: string;
    response?: any;
  }): Promise<void> {
    const errorReport: ErrorReport = {
      error: `API Error: ${error.method} ${error.endpoint} - ${error.status}`,
      stack: error.message,
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      additionalContext: {
        endpoint: error.endpoint,
        method: error.method,
        status: error.status,
        response: error.response,
      },
    };

    await this.logError(errorReport);
  }

  /**
   * Log une erreur réseau
   */
  async logNetworkError(error: {
    endpoint: string;
    method: string;
    message: string;
  }): Promise<void> {
    const errorReport: ErrorReport = {
      error: `Network Error: ${error.method} ${error.endpoint}`,
      stack: error.message,
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      additionalContext: {
        endpoint: error.endpoint,
        method: error.method,
        type: 'network',
      },
    };

    await this.logError(errorReport);
  }
}

// Export singleton
export const errorReportingService = new ErrorReportingService();
